import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  UserPlus,
  X,
  Save,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const USER_ROLES = [
  { value: 'owner', label: 'Agency Owner' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'staff', label: 'Staff' },
  { value: 'self_repair_customer', label: 'Self-Repair Customer' },
  { value: 'managed_client', label: 'Managed Client' },
];

/**
 * AddUserModal - Create new user under agency
 */
const AddUserModal = ({ isOpen, onClose, agencyId, agencyName, onSuccess }) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('self_repair_customer');
  const [isActive, setIsActive] = useState(true);
  const [sendInvite, setSendInvite] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setRole('self_repair_customer');
    setIsActive(true);
    setSendInvite(false);
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/create`,
        {
          agency_id: agencyId,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          name: `${firstName.trim()} ${lastName.trim()}`.trim() || email.split('@')[0],
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          role: role,
          is_active: isActive,
          send_invite: sendInvite,
        },
        { headers }
      );
      
      toast.success(`User ${firstName || email} created successfully`);
      resetForm();
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (err) {
      console.error('Create user error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to create user';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-lg bg-white border-slate-200" 
        data-testid="add-user-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <UserPlus className="text-amber-500" size={20} />
            Add New User
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Create a new user under <strong className="text-slate-800">{agencyName}</strong>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                First Name
              </label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className="h-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                data-testid="add-user-first-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Last Name
              </label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                className="h-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                data-testid="add-user-last-name"
              />
            </div>
          </div>
          
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="h-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
              required
              data-testid="add-user-email"
            />
          </div>
          
          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Phone
            </label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="555-123-4567"
              className="h-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
              data-testid="add-user-phone"
            />
          </div>
          
          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Role
            </label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger 
                className="h-10 bg-white border-slate-300 text-slate-900" 
                data-testid="add-user-role"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {USER_ROLES.map((r) => (
                  <SelectItem 
                    key={r.value} 
                    value={r.value}
                    className="text-slate-900 hover:bg-slate-100 focus:bg-slate-100"
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Status & Options */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-900 text-sm">Active Status</p>
                <p className="text-xs text-slate-500">User can access the system</p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
                data-testid="add-user-active"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-900 text-sm">Send Invite Email</p>
                <p className="text-xs text-slate-500">Email login credentials to user</p>
              </div>
              <Switch
                checked={sendInvite}
                onCheckedChange={setSendInvite}
                data-testid="add-user-send-invite"
              />
            </div>
          </div>
        </form>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose} 
            type="button"
            className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !email.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="add-user-submit"
          >
            {saving ? (
              <>
                <RefreshCw className="animate-spin mr-2" size={16} />
                Creating...
              </>
            ) : (
              <>
                <UserPlus size={16} className="mr-2" />
                Create User
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;
